// Client-side (browser) display helpers for buyer product subscriptions.
//
// The billing MATH lives server-side in functions/src/lib/subscriptions.ts
// (charge splits, cycle scheduling, dunning). This module is only the shared
// display vocabulary — types, labels, status styling, ordering, and date
// normalization — so the buyer's "My Subscriptions" page and the seller's
// "Subscribers" tab read a subscription identically instead of each keeping its
// own copy that can drift.

// Mirrors SUBSCRIPTION_STATUSES in functions/src/lib/subscriptions.ts.
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete';

// A subscription date field can arrive two ways depending on which one it is:
// `nextChargeAt` is written as a raw epoch-millis number (from addCycle), while
// `createdAt`/`canceledAt` are Firestore serverTimestamps and come back as
// Timestamp objects ({ seconds }). Callers should normalize with subscriptionMs
// before doing arithmetic or formatting — passing a Timestamp straight to
// formatUserDate renders blank, and subtracting two Timestamps yields NaN.
export type SubscriptionTimestamp = number | { seconds: number } | null | undefined;

export interface Subscription {
    id: string;
    productId: string;
    sellerId: string;
    subscriberUid: string;
    priceAud: number;
    frequency: string;
    currency: string;
    status: SubscriptionStatus;
    nextChargeAt?: SubscriptionTimestamp;
    chargeCount?: number;
    createdAt?: SubscriptionTimestamp;
    canceledAt?: SubscriptionTimestamp;
    /**
     * When a canceled subscription's access ends: the cycle boundary the buyer
     * had already paid for, i.e. what would have been the next billing date.
     * Written by cancelSubscriptionInternal, which stamps the same instant onto
     * users/{uid}/ownedProducts/{productId}. That copy is what actually gates
     * downloads. Absent while the subscription is still billing.
     */
    accessUntil?: SubscriptionTimestamp;
    isGuest?: boolean;
    /**
     * Set when the subscription came from a recurring TIER of a tiered product
     * rather than a whole `pricingModel: 'subscription'` product. `tierName` is
     * a snapshot taken at signup so the row still reads correctly after the
     * seller renames or removes the tier.
     */
    tierId?: string;
    tierName?: string;
    /**
     * A tier switch the buyer has scheduled, applied by the renewal scheduler at
     * `nextChargeAt` (see setSubscriptionTierChange in functions/src/index.ts).
     * Until then the subscription keeps billing `priceAud` on `tierId`: the buyer
     * has paid for the cycle they are in, so nothing about it changes.
     *
     * `pendingPriceAud` is the price captured when the switch was scheduled, and
     * is what the next charge will be, even if the seller reprices the tier in the
     * meantime.
     */
    pendingTierId?: string;
    pendingTierName?: string;
    pendingPriceAud?: number;
    pendingFrequency?: string;
    pendingRequestedAt?: SubscriptionTimestamp;
    /** When a scheduled switch was actually applied. Absent until one lands. */
    tierChangedAt?: SubscriptionTimestamp;
    /**
     * Consecutive failed renewal charges, written by the dunning path alongside
     * `status: 'past_due'` and reset once a charge succeeds. Drives how many
     * attempts are left before the subscription is canceled outright.
     */
    failureCount?: number;
    /**
     * A price change the SELLER made, applied by the renewal scheduler only once
     * `repriceEffectiveAt` has passed (see subscription-reprice.ts in functions).
     * Distinct from the `pending*` fields above in who initiated it: those are a
     * switch the buyer chose, these are a change being done to them, which is
     * why they carry a notice period and an email and a tier switch does not.
     *
     * Until the effective date the subscription keeps billing `priceAud`, so a
     * renewal that falls inside the notice window charges the old price.
     */
    repriceToAud?: number;
    repriceFromAud?: number;
    repriceEffectiveAt?: number;
    repriceNotifiedAt?: number;
    /** When a seller reprice was actually applied. Absent until one lands. */
    repricedAt?: number;
}

/** A seller price change waiting out its notice period. */
export interface PendingReprice {
    toAud: number;
    fromAud: number;
    effectiveAt: number;
}

/**
 * Read a scheduled seller reprice off a subscription, or null when there is no
 * usable one. Mirrors readPendingReprice in
 * functions/src/lib/subscription-reprice.ts; both refuse a half-written record
 * so the UI never announces a price the renewal path would not charge.
 */
export function readPendingReprice(
    sub: Pick<Subscription, 'repriceToAud' | 'repriceFromAud' | 'repriceEffectiveAt'>,
): PendingReprice | null {
    const toAud = Number(sub.repriceToAud);
    const fromAud = Number(sub.repriceFromAud);
    const effectiveAt = Number(sub.repriceEffectiveAt);
    if (!Number.isFinite(toAud) || toAud <= 0) return null;
    if (!Number.isFinite(fromAud) || fromAud <= 0) return null;
    if (!Number.isFinite(effectiveAt) || effectiveAt <= 0) return null;
    return { toAud, fromAud, effectiveAt };
}

/** The scheduled switch on a subscription, or null when there isn't a usable one. */
export interface PendingTierChange {
    tierId: string;
    tierName: string;
    priceAud: number;
    frequency: string;
}

export function readPendingTierChange(
    sub: Pick<Subscription, 'pendingTierId' | 'pendingTierName' | 'pendingPriceAud' | 'pendingFrequency'>,
): PendingTierChange | null {
    if (!sub.pendingTierId) return null;
    const priceAud = Number(sub.pendingPriceAud);
    if (!Number.isFinite(priceAud) || priceAud <= 0) return null;
    return {
        tierId: sub.pendingTierId,
        tierName: sub.pendingTierName ?? '',
        priceAud,
        frequency: sub.pendingFrequency ?? 'monthly',
    };
}

/**
 * True when this subscription can still have its tier switched. Mirrors the
 * status gate in setSubscriptionTierChange: only a subscription that will reach
 * another billing boundary has a boundary to apply a change at, and only a
 * TIERED one has tiers to move between.
 */
export function canChangeTier(
    sub: Pick<Subscription, 'status' | 'tierId'>,
): boolean {
    return !!sub.tierId && (sub.status === 'active' || sub.status === 'past_due');
}

// ---------------------------------------------------------------------------
// Subscription eligibility gate (client mirror of the server rule)
// ---------------------------------------------------------------------------

/**
 * Minimal shape of the auth state the gate reads. Typed structurally rather
 * than against FirebaseUser so every caller can pass exactly what it already
 * holds from useAuth() without importing firebase types.
 */
export interface SubscriptionEligibility {
    authUser?: { isAnonymous?: boolean; emailVerified?: boolean } | null;
    user?: { emailVerified?: boolean } | null;
}

export type SubscriptionBlockReason = 'signed-out' | 'unverified';

/**
 * Why this buyer cannot start a subscription, or null when they can.
 *
 * Mirrors the guest test in createAirwallexPaymentIntent
 * (functions/src/index.ts), which refuses a subscription checkout outright for
 * signed-out, anonymous, and email-unverified buyers. A subscription needs a
 * real account because cancelling, repairing a failed renewal, and the
 * one-live-subscription-per-product guard all key off the buyer's uid, and
 * there is no uid on a guest order. The server stays authoritative; this exists
 * so the UI stops the buyer at the Subscribe button instead of walking them
 * through checkout to a dead end at the payment step.
 *
 * The Firestore profile's `emailVerified` is accepted alongside the Firebase
 * user's flag because that mirror is written by the Admin SDK and can already
 * be true while the browser's ID token still carries a stale claim. The server
 * absorbs the same lag by re-reading admin.auth(), so honouring it here only
 * ever unblocks a buyer the server would have accepted anyway.
 */
export function subscriptionBlockReason({
    authUser,
    user,
}: SubscriptionEligibility): SubscriptionBlockReason | null {
    if (!authUser || authUser.isAnonymous) return 'signed-out';
    if (authUser.emailVerified === true || user?.emailVerified === true) return null;
    return 'unverified';
}

export const canStartSubscription = (auth: SubscriptionEligibility): boolean =>
    subscriptionBlockReason(auth) === null;

/**
 * One copy deck for the gate so the product page, cart, and checkout give the
 * buyer the same reason and the same way out.
 */
export const SUBSCRIPTION_GATE_COPY: Record<
    SubscriptionBlockReason,
    { title: string; body: string; cta: string; href: string }
> = {
    'signed-out': {
        title: 'Log in to subscribe',
        body: 'Subscriptions need an account so you can cancel any time, update your card if a renewal fails, and keep access across renewals.',
        cta: 'Create an account',
        href: '/signup',
    },
    unverified: {
        title: 'Verify your email to subscribe',
        body: 'Recurring payments need a verified email so renewal receipts reach you and the subscription stays manageable from your dashboard.',
        cta: 'Verify your email',
        href: '/awaiting-verification',
    },
};

/**
 * Destination for the gate's CTA, carrying a `?redirect=` back to where the
 * buyer was (the product, the cart, checkout), so finishing the step returns
 * them to the purchase instead of dumping them on the dashboard. Both
 * destinations honour it, through safeRedirectTarget: /signup once the account
 * exists (email and OAuth sign-ups alike, and /login when they switch to it),
 * and /awaiting-verification once the email is verified. It used to be dropped
 * for the unverified case on the belief that /awaiting-verification took no
 * redirect; it does, and without one a buyer who verified mid-purchase was
 * sent to the dashboard and had to find their way back.
 */
export function subscriptionGateHref(
    reason: SubscriptionBlockReason,
    returnTo?: string,
): string {
    const { href } = SUBSCRIPTION_GATE_COPY[reason];
    if (!returnTo) return href;
    return `${href}?redirect=${encodeURIComponent(returnTo)}`;
}

/**
 * Normalize any subscription date field to epoch millis. Handles the raw-number
 * form (nextChargeAt) and the Firestore Timestamp form (createdAt/canceledAt).
 * Returns null when the value is absent or unparseable — safe to pass straight
 * to formatUserDate, which treats null as "".
 */
export function subscriptionMs(value: SubscriptionTimestamp): number | null {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'object' && typeof value.seconds === 'number') {
        return value.seconds * 1000;
    }
    return null;
}

// ---------------------------------------------------------------------------
// Billing history
// ---------------------------------------------------------------------------

/**
 * One charge in a subscription's billing history, read from
 * users/{uid}/purchases. Both kinds of recurring billing write there, so the
 * same shape covers a Diamond membership (`type: 'diamond'`) and a creator
 * subscription (the checkout receipt for the first charge, then one
 * `type: 'subscription'` doc per renewal).
 *
 * The doc id is the payment intent id, which is also the receipt key, so `id`
 * alone is enough to link a row to its receipt.
 */
export interface SubscriptionPayment {
    id: string;
    /** What the card was actually debited: price + processing fee + tax. */
    amount: number;
    /** The per-cycle price before fees and tax. Absent on legacy receipts. */
    subtotal?: number;
    currency?: string;
    createdAt?: SubscriptionTimestamp;
    /**
     * The subscription this charge renewed. Written on renewal receipts only:
     * a first charge needs no back-reference because its purchase doc id IS the
     * subscription id (both are the first charge's payment intent id).
     */
    subscriptionId?: string;
    /**
     * The card (or wallet) the charge was taken from, as the provider reported
     * it at the time. Present on a CHECKOUT receipt — the first charge of a
     * subscription and the first Diamond charge — and absent on renewal
     * receipts, which are taken against the stored consent rather than a fresh
     * payment method.
     *
     * That absence is not a gap: a subscription is charged against one consent
     * for its whole life, so the card named on the first charge IS the card the
     * renewals hit. subscriptionPaymentMethod() is what callers should use, and
     * it reads exactly that.
     */
    paymentMethod?: { type: string; brand: string; last4: string } | null;
}

/**
 * The card a subscription is billed on, or null when no charge on record names
 * one. Reads the OLDEST charge that carries a payment method, which is the
 * checkout receipt: renewals reuse that charge's consent, so it stays accurate
 * for the life of the subscription and is the card a failed renewal is failing
 * on.
 *
 * Expects `payments` newest first (comparePaymentsDesc).
 */
export function subscriptionPaymentMethod(
    payments: SubscriptionPayment[],
): { type: string; brand: string; last4: string } | null {
    for (let i = payments.length - 1; i >= 0; i -= 1) {
        const method = payments[i]?.paymentMethod;
        if (method && (method.last4 || method.brand)) return method;
    }
    return null;
}

/**
 * How a payment method reads on screen, e.g. "Visa ···· 4242". Wallet methods
 * (Link, Apple Pay) carry no usable brand/last4 pair, so they are named by type
 * instead of rendering a card row with empty digits.
 */
export function describePaymentMethod(
    method: { type: string; brand: string; last4: string } | null | undefined,
): string | null {
    if (!method) return null;
    if (method.type && method.type !== 'card' && !method.last4) return method.type;
    if (!method.last4) return method.brand || null;
    return `${method.brand || 'Card'} ···· ${method.last4}`;
}

/** Receipt URL for a charge. Matches the links in the library's payment history. */
export const receiptHref = (paymentIntentId: string): string =>
    `/receipt?payment_intent=${encodeURIComponent(paymentIntentId)}&redirect_status=succeeded`;

/** Newest charge first: the order billing history is read in. */
export const comparePaymentsDesc = (a: SubscriptionPayment, b: SubscriptionPayment): number =>
    (subscriptionMs(b.createdAt) ?? 0) - (subscriptionMs(a.createdAt) ?? 0);

/**
 * What the next charge will actually debit, in AUD.
 *
 * The stored `priceAud` is the per-cycle price the buyer agreed to, but the card
 * is debited that plus the processing fee and any consumption tax, so quoting
 * `priceAud` as the upcoming charge understates it, and disagrees with every
 * amount in the billing history right below it. The last charge is the honest
 * estimate: the price is locked for the life of the subscription, the fee is
 * derived from it, and the tax comes from a country that does not change
 * between cycles.
 *
 * Expects `payments` newest first (comparePaymentsDesc), since it reads the most
 * recent matching charge.
 *
 * Only a charge whose `subtotal` still matches the current price is used, so a
 * legacy receipt (no subtotal) or one taken at a different price falls back to
 * the price rather than quoting a figure that no longer applies.
 */
export function expectedChargeAmount(
    priceAud: number,
    payments: SubscriptionPayment[],
): number {
    if (!Number.isFinite(priceAud)) return 0;
    const latest = payments.find(p =>
        typeof p.subtotal === 'number' &&
        Math.abs(p.subtotal - priceAud) < 0.005 &&
        Number.isFinite(p.amount) &&
        p.amount > 0,
    );
    return latest ? latest.amount : priceAud;
}

// ---------------------------------------------------------------------------
// Dunning (a renewal charge that failed)
// ---------------------------------------------------------------------------

// Mirrors DUNNING_MAX_ATTEMPTS / DUNNING_RETRY_DAYS in
// functions/src/lib/subscriptions.ts. The server stays authoritative — it is
// what actually schedules the retries and cancels — but the buyer is emailed and
// deep-linked to the subscriptions page when a renewal fails, so the page has to
// be able to tell them what happens next and when.
export const DUNNING_MAX_ATTEMPTS = 3;
export const DUNNING_RETRY_DAYS = 2;

export interface DunningState {
    /** Consecutive failures so far, including the one that just happened. */
    attemptsUsed: number;
    /** How many charge attempts are left before the subscription is canceled. */
    attemptsRemaining: number;
    /** When the next attempt is scheduled, or null when it isn't known. */
    retryAtMs: number | null;
    /**
     * When the subscription will be canceled if every remaining attempt also
     * fails, or null when the retry date isn't known. The last attempt IS the
     * cancellation point: a failure at DUNNING_MAX_ATTEMPTS cancels rather than
     * scheduling another retry.
     */
    cancelsAtMs: number | null;
}

/**
 * What a past-due subscription is waiting on, or null when it is not past due.
 *
 * This exists because a failed renewal is the one subscription state the buyer
 * has to act on, and the only thing the page showed was an amber "Past due"
 * pill: no reason, no retry date, and no warning that the subscription is on its
 * way to being canceled.
 *
 * `failureCount` is absent on subscriptions that went past due before it was
 * recorded; those are treated as one failure, which is the floor implied by
 * being past due at all.
 */
export function dunningState(
    sub: Pick<Subscription, 'status' | 'failureCount' | 'nextChargeAt'>,
): DunningState | null {
    if (sub.status !== 'past_due') return null;
    const raw = Number(sub.failureCount);
    const attemptsUsed = Number.isFinite(raw) && raw > 0
        ? Math.min(Math.floor(raw), DUNNING_MAX_ATTEMPTS)
        : 1;
    const attemptsRemaining = Math.max(0, DUNNING_MAX_ATTEMPTS - attemptsUsed);
    const retryAtMs = subscriptionMs(sub.nextChargeAt);
    // Each further attempt is DUNNING_RETRY_DAYS apart, and the LAST one is the
    // one that cancels, so the final attempt is (remaining - 1) gaps after the
    // next one rather than `remaining` gaps.
    const cancelsAtMs = retryAtMs != null && attemptsRemaining > 0
        ? retryAtMs + (attemptsRemaining - 1) * DUNNING_RETRY_DAYS * 24 * 60 * 60 * 1000
        : retryAtMs;
    return { attemptsUsed, attemptsRemaining, retryAtMs, cancelsAtMs };
}

// ---------------------------------------------------------------------------
// Access expiry after cancellation
// ---------------------------------------------------------------------------

/**
 * Client mirror of isAccessExpired in functions/src/lib/subscriptions.ts.
 *
 * Absent/zero `accessUntil` means "never expires": every entitlement from a
 * one-time purchase, and every subscription that is still billing. Only
 * cancellation stamps a boundary. The server stays authoritative (the download
 * API re-checks it); this exists so the library, product page, and dashboard
 * stop offering access the buyer no longer has.
 */
export function isAccessExpired(accessUntil: unknown, nowMs: number): boolean {
    const until = Number(accessUntil);
    return Number.isFinite(until) && until > 0 && until <= nowMs;
}

/** What a canceled subscription's access line should say, or null when there is
 *  nothing to say (the subscription is still billing, or predates accessUntil
 *  and has no boundary to report). */
export interface SubscriptionAccessState {
    ms: number;
    expired: boolean;
}

/**
 * Resolve a canceled subscription's access boundary for display, or null when
 * there is none to state.
 *
 * Deliberately does NOT fall back to `nextChargeAt` for subscriptions canceled
 * before `accessUntil` was introduced. That field still holds the boundary those
 * buyers paid through, so it would render a plausible date, but their
 * entitlement carries no matching stamp and their access does not in fact
 * expire. Callers should keep showing those the plain cancellation date rather
 * than announce a deadline that will never be enforced.
 */
export function subscriptionAccessState(
    sub: Pick<Subscription, 'status' | 'accessUntil'>,
    nowMs: number,
): SubscriptionAccessState | null {
    if (sub.status !== 'canceled') return null;
    const ms = subscriptionMs(sub.accessUntil);
    if (ms == null) return null;
    return { ms, expired: ms <= nowMs };
}

// Human-friendly recurrence labels. Kept in sync with the cart/checkout wording
// so a buyer reads the same cadence from checkout through to management, and the
// seller reads the same wording on the subscribers tab.
export const SUBSCRIPTION_LABELS: Record<string, string> = {
    weekly: 'weekly',
    fortnightly: 'fortnightly',
    monthly: 'monthly',
    '3-months': 'quarterly',
    annually: 'annually',
};

export const formatRecurring = (frequency?: string): string => {
    if (!frequency) return 'recurring';
    return SUBSCRIPTION_LABELS[frequency] ?? frequency;
};

// Cadence word shown directly after a recurring price, e.g.
// formatCurrency(9.99) + " monthly". Used by the product page and product cards
// (whole-product subscriptions and recurring tiers alike) and the seller's
// product previews. Deliberately the same wording as SUBSCRIPTION_LABELS above
// and never a count ("fortnightly", not "/2 weeks"), so a buyer reads one
// cadence vocabulary from the card through to subscription management.
export const formatCycleSuffix = (frequency?: string): string =>
    SUBSCRIPTION_LABELS[frequency ?? 'monthly'] ?? 'monthly';

// Factor that converts one per-cycle charge to a monthly-equivalent amount, so a
// mixed set of cadences can be summed into a single MRR figure. Weekly and
// fortnightly scale up (more than one charge a month); quarterly and annual scale
// down. Mirrors SUBSCRIPTION_FREQUENCIES in functions/src/lib/subscriptions.ts.
export const SUBSCRIPTION_MONTHLY_FACTOR: Record<string, number> = {
    weekly: 52 / 12,
    fortnightly: 26 / 12,
    monthly: 1,
    '3-months': 1 / 3,
    annually: 1 / 12,
};

/**
 * Monthly-equivalent value (AUD) of a single subscription's per-cycle price.
 * Sum this across active subscriptions to get MRR. Unknown/absent cadence or a
 * non-finite price contributes 0 rather than NaN-poisoning the total.
 */
export function toMonthlyAud(priceAud: number, frequency: string): number {
    const factor = SUBSCRIPTION_MONTHLY_FACTOR[frequency];
    if (!factor || !Number.isFinite(priceAud)) return 0;
    return priceAud * factor;
}

export const SUBSCRIPTION_STATUS_META: Record<
    SubscriptionStatus,
    { label: string; variant: 'success' | 'warning' | 'destructive' | 'muted' }
> = {
    active: { label: 'Active', variant: 'success' },
    past_due: { label: 'Past due', variant: 'warning' },
    canceled: { label: 'Cancelled', variant: 'muted' },
    incomplete: { label: 'Incomplete', variant: 'muted' },
};

/**
 * Ordering shared by the buyer list and the seller subscribers tab: live
 * subscriptions (anything not canceled) come first, ordered by soonest next
 * charge; canceled ones fall to the bottom, most recently canceled first.
 * Dates are read through subscriptionMs so the arithmetic is valid whether the
 * field is a number or a Firestore Timestamp.
 */
export function compareSubscriptions(a: Subscription, b: Subscription): number {
    const aDone = a.status === 'canceled';
    const bDone = b.status === 'canceled';
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (aDone) return (subscriptionMs(b.canceledAt) ?? 0) - (subscriptionMs(a.canceledAt) ?? 0);
    return (subscriptionMs(a.nextChargeAt) ?? Infinity) - (subscriptionMs(b.nextChargeAt) ?? Infinity);
}

/**
 * The MRR / active / churn figures the seller analytics page shows.
 *
 * Split out of the page so the churn arithmetic can be tested, because it was
 * wrong in a way a rendered percentage will never reveal.
 *
 * MRR and the active count are POINT-IN-TIME: a subscriber who is billing counts
 * whatever window is on screen. `past_due` counts as active because the buyer
 * still has access while dunning retries.
 *
 * Churn is time-scoped, and its denominator is the number of subscriptions that
 * were live when the window OPENED. The previous formula used the CURRENT active
 * count plus the cancellations, so every signup made during the window inflated
 * the base and understated the rate; on an unbounded window it degenerated into
 * every cancellation ever over every subscriber ever, which is a lifetime
 * retention ratio and not churn at all.
 *
 * A subscription counts toward the base when it began before the window and had
 * not already been canceled by then. Rows with no `createdAt` are treated as
 * pre-existing, which is what an older record is.
 */
export function summariseSubscriptions(
    subs: Subscription[],
    window: { fromMs: number; toMs: number },
): {
    activeCount: number;
    pastDueCount: number;
    mrr: number;
    canceledInPeriod: number;
    churnRate: number;
} {
    const { fromMs, toMs } = window;
    const live = subs.filter(s => s.status === 'active' || s.status === 'past_due');

    const canceledInPeriod = subs.filter(s => {
        if (s.status !== 'canceled') return false;
        const ms = subscriptionMs(s.canceledAt);
        return ms != null && ms >= fromMs && ms <= toMs;
    }).length;

    const churnBase = subs.filter(s => {
        const startedMs = subscriptionMs(s.createdAt);
        if (startedMs != null && startedMs > fromMs) return false; // began mid-window
        if (s.status !== 'canceled') return true;
        const canceledMs = subscriptionMs(s.canceledAt);
        return canceledMs == null || canceledMs >= fromMs; // still live at the open
    }).length;

    return {
        activeCount: live.length,
        pastDueCount: live.filter(s => s.status === 'past_due').length,
        mrr: live.reduce((sum, s) => sum + toMonthlyAud(s.priceAud || 0, s.frequency), 0),
        canceledInPeriod,
        churnRate: churnBase > 0 ? (canceledInPeriod / churnBase) * 100 : 0,
    };
}
